// The welcome card's opening line on a tank with no readings yet, by what
// the tank is doing. Until 2026-10-01 tank.phase was null for a tank's
// whole first session (no score row yet), so a two-week-old tank was told
// "Alk + salinity is a great place to start" instead of the cycling line;
// a freshwater tank got the same reef line.

import { tankSubtype } from '../../shared/tank-subtype.js';
import { FRESHWATER_SUBTYPES } from '../../shared/param-explainers.js';
import { isQuarantineTank } from '../../shared/tank-purpose.js';

export function isFreshwaterTank(tank) {
  return FRESHWATER_SUBTYPES.includes(String(tankSubtype(tank) || ''));
}

export function isCyclingPhase(phase) {
  return phase === 'cycling' || phase === 'setup';
}

export function firstRunHint(tank) {
  const phase = tank?.phase || null;
  if (isQuarantineTank(tank)) {
    return "A quarantine tank: log temperature, ammonia and nitrite as you test them. Start a quarantine from Today to keep each fish's daily log and photos in one place.";
  }
  if (isCyclingPhase(phase)) {
    return tank?.guided_setup
      ? "You're cycling. It often takes several weeks, and it's about patience: let ammonia, nitrite and nitrate rise and fall on their own. Test 2-3 times a week so we can see the cycle finish."
      : 'Your tank is cycling. Log your first ammonia, nitrite and nitrate readings so Sensei can track the cycle.';
  }
  if (!tank?.established_date) {
    return 'Once the tank has water in it, set its start date in Tank settings and log your first reading. Sensei follows the cycle from there.';
  }
  if (!phase) return 'Log your first reading so Sensei can start a baseline.';
  if (isFreshwaterTank(tank)) {
    return 'Log your first reading so Sensei can start a baseline. Temperature, pH and nitrate are a good place to start.';
  }
  if (tankSubtype(tank) === 'fowlr') {
    return 'Log your first reading so Sensei can start a baseline. Salinity, temperature and nitrate are a good place to start.';
  }
  return 'Log your first reading so Sensei can start a baseline. Alk + salinity is a great place to start.';
}

// Stages a keeper can pin in Tank settings ("Set the stage"), each with one
// line of what it means. The lines restate the server's own rule
// (server/score.js getPhase): setup is the first two days with no test,
// cycling runs until ammonia has shown and cleared and ammonia and
// nitrite read near zero with nitrate showing on two tests a day apart, and a reef tank then goes maturing (no coral, under 6 months)
// -> growing -> established at a year. Freshwater and fish-only tanks skip
// maturing and growing (keepsNoCoral in server/task-fit.js, read on the
// tank type as getPhase reads it, tankSubtype: an older tank with only a type
// label too), so those two are not offered there, unless one is already
// pinned, so the keeper can see it and clear it.
const STAGE_LINES = {
  setup: 'Just filled. Nothing tested yet.',
  cycling:
    'The cycle is running until ammonia has shown and cleared, and ammonia and nitrite read near zero with nitrate showing on two tests a day or more apart.',
  maturing: 'Cycled, under 6 months old, no coral yet.',
  growing: 'Cycled and under a year old, with coral in or past 6 months.',
  established: 'A year old or more.',
};
const STAGE_LABELS = {
  setup: 'Setup',
  cycling: 'Cycling',
  maturing: 'Maturing',
  growing: 'Growing',
  established: 'Established',
};

export function skipsReefStages(tank) {
  const slug = String(tankSubtype(tank) || '');
  return FRESHWATER_SUBTYPES.includes(slug) || slug === 'fowlr';
}

export function stageOptions(tank) {
  const noReef = skipsReefStages(tank);
  const pinned = tank?.phase_override || null;
  return Object.keys(STAGE_LABELS)
    .filter((k) => !noReef || (k !== 'maturing' && k !== 'growing') || k === pinned)
    .map((k) => {
      let line = STAGE_LINES[k];
      if (noReef && k === 'established')
        line = 'Cycled. This kind of tank goes here once the cycle is done.';
      if (noReef && (k === 'maturing' || k === 'growing'))
        line = 'A reef stage. This kind of tank skips it.';
      return { k, label: STAGE_LABELS[k], line };
    });
}

export function stageLabel(phase) {
  return STAGE_LABELS[phase] || null;
}
